/**
 * Ce que l'écran dit quand le Pack IA ne répond pas.
 *
 * Le serveur distingue quatre refus, et chacun appelle une autre réaction de
 * l'utilisateur : rien à faire si le serveur n'a pas de clé, souscrire si la
 * société n'a pas l'option, attendre si la part de l'heure est épuisée,
 * réessayer si le modèle a répondu de travers. Le texte du serveur est déjà en
 * français ; on y ajoute seulement ce que l'utilisateur peut faire.
 */

/** Ce que l'on dit quand le serveur n'a pas de clé. */
export const MESSAGE_NON_ACTIVE =
  "Le Pack IA n'est pas activé sur ce serveur. Vous pouvez continuer à la main ; " +
  "l'exploitant peut l'activer en renseignant une clé.";

/**
 * Traduit une erreur du Pack IA en phrase pour l'écran.
 *
 * @param {Error & {status?: number}} erreur erreur rendue par le client HTTP
 * @returns {string} ce qu'il faut afficher
 */
export function messageIa(erreur) {
  const statut = erreur?.status ?? 0;
  const detail = erreur?.message || '';
  if (statut === 503 && /pas activé/.test(detail)) return MESSAGE_NON_ACTIVE;
  if (statut === 503) return detail || "Le service d'IA est indisponible pour l'instant.";
  if (statut === 403)
    return (detail || 'Pack IA non inclus dans ta formule.') + ' Souscris-le depuis Abonnement.';
  if (statut === 429)
    return detail || 'La limite horaire du Pack IA est atteinte. Réessaie plus tard.';
  if (statut === 502)
    return (detail || 'Le modèle a répondu de travers.') + ' Réessaie, ou rédige à la main.';
  if (statut === 400) return detail || 'La demande a été refusée.';
  if (statut === 0 && erreur) return detail || 'Pas de connexion au serveur.';
  return "L'assistant n'a pas pu répondre. Réessaie dans un instant.";
}

/**
 * Sépare une adresse de données en son type et son contenu encodé.
 *
 * @param {string} adresse valeur `data:type;base64,…` telle que le navigateur la fabrique
 * @returns {{typeMedia: string, contenu: string}} type de média et base64 nu
 */
export function decoupeAdresseDeDonnees(adresse) {
  const m = /^data:([^;,]+);base64,(.*)$/s.exec(String(adresse || ''));
  if (!m) return { typeMedia: '', contenu: String(adresse || '') };
  return { typeMedia: m[1].toLowerCase(), contenu: m[2] };
}

/**
 * Confiance du modèle, en phrase.
 *
 * @param {number} confiance de 0 à 1
 * @returns {string} ce qu'on dit à l'utilisateur avant qu'il enregistre
 */
export function phraseDeConfiance(confiance) {
  const pct = Math.round(Math.max(0, Math.min(1, Number(confiance) || 0)) * 100);
  if (pct >= 85) return `Ticket lu avec ${pct} % de confiance — vérifie le montant et la date.`;
  if (pct >= 50)
    return `Lecture incertaine (${pct} %) — contrôle chaque champ avant d'enregistrer.`;
  return `Le ticket est difficile à lire (${pct} %) — complète le formulaire toi-même.`;
}
